import Notifications from 'react-notification-system-redux';
import { api } from '../../../general/apiScenarios';
import { BLOptions, CreativeFolderServer } from '../../../general/modelCreativeFolder';
import { Interrupt, TSagaReturn } from '../../../general/scenarioFactory';
import { scenarioFactoryAsync } from '../../../general/scenarioFactoryAsync';
import { ListenerApiLight, _select, _take } from '../../listenerMiddleware';
import { obtainUsers } from '../app/sagas';
import * as creativeFolderActions from './actions';
import * as appActions from '../app/actions';
import { BLContext, Messages, WithMultipleValues } from './model';
import { getBLOptions } from './selectors';
import { uniqBy } from 'lodash';
import uniq from 'lodash/uniq';
import { APIScenarioReturn } from '../../../general/apiSagaFactory';
import { NO_TEAM, AnyUser, Permission } from '../../../general/model';
import {
  PlotRecognitionInfo,
  diffPlotRecognition,
  getPlotRecognition,
  getPlotRecognitionInfo,
} from '../../../general/modelPlotRecognition';
import {
  CreativeFolderStrict,
  WithTags,
  getBLOptionsFromCreative,
  makeCreativeFolder,
} from '../../../general/modelCreativeFolder';
import { CHNode, CHNodeIsInTest } from '../../../general/node';
import { allSuccessfulApiScenarioReturn } from '../../../general/scenarioFactory';
import { nonUndefined } from '../../../general/utils/nonNull';
import { lm } from '../../listenerMiddleware';
import { getUser } from '../app/selectors';
import * as fileManagerActions from '../fileManager/actions';
import { getSelectedNodesInCurrentFolder } from '../fileManager/selectors/selectors';
import { editButtonClicked } from './actions';
import { MultipleValues, MultipleValuesSymbol, TagsMode } from './model';
import { CurrentUser } from '../../../general/model';

export function creativeFolderRootListener() {
  lm.takeEvery(editButtonClicked, async (_, lapi) => {
    const nodes = _select(lapi, getSelectedNodesInCurrentFolder);
    const user = _select(lapi, getUser);
    if (!user) {
      return;
    }
    const inTest = nodes.some(CHNodeIsInTest);
    const tagsMode = nodes.length !== 1 ? TagsMode.ADD : TagsMode.EDIT;
    const creatives = await Promise.all(
      nodes.map((n) => api.creative.loadCreativeForNodeScenario(n.id)),
    );

    if (!allSuccessfulApiScenarioReturn(creatives)) {
      lapi.dispatch(Notifications.error(Messages.creativeLoadFailed()));
      return;
    }

    // Preload users from creatives
    const users = extractUsersFromNodes(creatives.map((x) => x.data.response[0]));
    // Notify dialog that users are preloaded
    lapi.dispatch(appActions.usersPreloaded({ users }));

    const creativeFolders = creatives
      .map((x) => x.data.response[0])
      .map((x) => makeCreativeFolder(x, null));

    const blOptsArray = creativeFolders.map(getBLOptionsFromCreative);
    const commonBLOpts = getCommonBlOptions(blOptsArray, tagsMode);
    // Права `admin` в Creative Hub — то же право, что открывает админский раздел.
    const isAdmin =
      user.is_superuser === true || user.permissions.includes(Permission['fs.crhub_admin_access']);
    const editableFields = getEditableFields(inTest, nodes, isAdmin);
    // Справочная информация о разметке имеет смысл только для одиночного креатива.
    const plotRecognitionInfo =
      creativeFolders.length === 1
        ? getPlotRecognitionInfo(creativeFolders[0].plot_recognition)
        : null;
    const confirmSaga = async (data: WithMultipleValues<BLOptions>) =>
      patchCreatives(lapi, creativeFolders, data, tagsMode);

    const scenarioResult = await creativeFolderModalScenario(
      lapi,
      commonBLOpts,
      plotRecognitionInfo,
      editableFields,
      confirmSaga,
    );

    if (!scenarioResult.success) {
      if (scenarioResult.reason !== Interrupt.UserCancelled) {
        lapi.dispatch(
          Notifications.error(Messages.creativeFolderScenarioError(scenarioResult.reason)),
        );
      }
      return;
    }

    lapi.dispatch(fileManagerActions.refreshRequested());
  });
}

const extractUsersFromNodes = (creatives: CreativeFolderServer[]) => {
  const users: AnyUser[] = [];
  creatives.forEach((creative) => {
    if (creative.executor) {
      users.push(creative.executor);
    }

    if (creative.customer) {
      users.push(creative.customer);
    }
  });
  // Remove duplicates by user id
  return uniqBy(users, 'id');
};

const getEditableFields = (
  isTest: boolean,
  nodes: CHNode[],
  isAdmin: boolean,
  // breadcrumbs: CHNode[],
  // user: CurrentUser,
): (keyof BLOptions)[] => {
  // 1. Если креатив не отправлен в тест предоставить возможность выбрать один
  //    или несколько креативов и при нажатии кнопки "карандаш" дать возможность
  //    изменять исполнителя, менеджера, добавить тэг, переименовать креатив(если
  //    выбрано несколько креативов поле ввода имени заблокировано)
  // 2. Если креатив отправлен в тест предоставить возможность выбрать один или
  //    несколько креативов и при нажатии кнопки "карандаш" дать возможность
  //    редактировать поля: исполнитель, менеджер, добавить тэг. Поле ввода имени
  //    креативов заблокировано.

  const alwaysEnabled: (keyof BLOptions)[] = [
    'developerId',
    'managerId',
    'tagsActive',
    'teamID',
    'genres',
    'intro',
    'mislead_level',
    'ai_generated',
    'concept_type',
  ];
  const isMultiple = nodes.length > 1;

  const _fields: (keyof BLOptions)[] = [...alwaysEnabled];
  // Параметры AI-разметки правит только `admin` и только у одиночного креатива:
  // данные привязаны к креативу целиком, а массовая правка сразу зафиксировала бы
  // override на всех выбранных креативах (MYUA-16423). Статус теста тут не важен.
  if (isAdmin && !isMultiple) {
    _fields.push('plot_recognition');
  }
  if (isTest) {
    return _fields;
  }
  if (!isMultiple) {
    _fields.push('creativeName');
    _fields.push('source_creatives');
  }
  return _fields;
};

/**
 * возвращает true, если все values одинаковы (через ===)
 * Не работает с массивами!
 * @param values
 */
const same = (values: any[]) => values.every((v) => v === values[0]);

/**
 * Возвращает значение поля, если оно одинаково среди всех кандидатов, или `MultipleValues`
 * @param candidates Список объектов, из которых взять общее значение
 * @param field Поле, значение которого будем брать
 */
const getCommon = function <T, Field extends keyof T>(
  candidates: T[],
  field: Field,
): T[Field] | MultipleValues {
  const values: T[Field][] = candidates.map((x) => x[field]);
  if (same(values)) {
    return values[0];
  }
  return MultipleValuesSymbol;
};

/**
 * Вариант getCommon для полей-массивов. Сравнивает содержимое массивов
 * поэлементно (порядок неважен). Если все массивы совпадают — возвращает
 * копию первого, иначе `MultipleValuesSymbol`.
 */
const getCommonArray = function (candidates: number[][]): number[] | MultipleValues {
  if (candidates.length === 0) return [];
  const first = [...candidates[0]].sort((a, b) => a - b);
  for (let i = 1; i < candidates.length; i++) {
    const current = [...candidates[i]].sort((a, b) => a - b);
    if (current.length !== first.length) return MultipleValuesSymbol;
    for (let j = 0; j < current.length; j++) {
      if (current[j] !== first[j]) return MultipleValuesSymbol;
    }
  }
  return [...first];
};

// type Stripped<T> = T extends WithMultipleValues<infer U> ? Partial<U> : any;
const stripMultipleValues = function <T extends Record<string, any>>(
  obj: WithMultipleValues<T>,
): Partial<T> {
  const keys: (keyof T)[] = Object.keys(obj);
  const result: Partial<T> = {};
  for (const key of keys) {
    const value = obj[key];
    if (value !== MultipleValuesSymbol) {
      result[key] = value as Partial<T>[keyof T];
    }
  }
  return result;
};

const getCommonBlOptions = (
  candidates: BLOptions[],
  tagsMode: TagsMode,
): WithMultipleValues<BLOptions> => {
  return {
    intro: getCommon(candidates, 'intro'),
    creativeName: getCommon(candidates, 'creativeName'),
    creativeTypeId: getCommon(candidates, 'creativeTypeId'),
    developerId: getCommon(candidates, 'developerId'),
    developer: getCommon(candidates, 'developer'),
    managerId: getCommon(candidates, 'managerId'),
    manager: getCommon(candidates, 'manager'),
    projectId: getCommon(candidates, 'projectId'),
    mislead_level: getCommon(candidates, 'mislead_level'),
    ai_generated: getCommon(candidates, 'ai_generated'),
    concept_type: getCommon(candidates, 'concept_type'),
    genres: getCommonArray(candidates.map((c) => c.genres)),
    tagsActive: tagsMode === TagsMode.ADD ? [] : candidates[0].tagsActive,
    teamID: getCommon(candidates, 'teamID'),
    source_creatives: getCommon(candidates, 'source_creatives'),
    // Разметка привязана к креативу целиком — общего значения для нескольких нет.
    plot_recognition:
      candidates.length === 1 ? candidates[0].plot_recognition : MultipleValuesSymbol,
  };
};

async function patchCreatives(
  lapi: ListenerApiLight,
  creativeFolders: WithTags<CreativeFolderStrict>[],
  scenarioResultData: WithMultipleValues<BLOptions>,
  tagsMode: TagsMode,
): Promise<boolean> {
  const resultData = stripMultipleValues(scenarioResultData);
  const results = await Promise.all(
    creativeFolders.map((creativeFolder) =>
      api.creative.patchCreativeScenario(creativeFolder.id.toString(10), {
        ...resultData,
        tagsActive:
          tagsMode === TagsMode.ADD
            ? uniq([...creativeFolder.tags, ...(resultData.tagsActive ?? [])])
            : resultData.tagsActive ?? [],
        teamID: resultData.teamID === NO_TEAM ? null : resultData.teamID,
        // Отправляем только реально изменённые поля разметки: каждое присланное поле
        // бэкенд навсегда помечает как правленное вручную (MYUA-16423).
        plot_recognition: resultData.plot_recognition
          ? diffPlotRecognition(
              getPlotRecognition(creativeFolder.plot_recognition),
              resultData.plot_recognition,
            )
          : undefined,
      }),
    ),
  );
  const ok = allSuccessfulApiScenarioReturn(results as APIScenarioReturn[]);

  if (!ok) {
    lapi.dispatch(Notifications.error(Messages.creativeUpdateFailed()));

    const errors = results
      .map((item) => (item.success ? undefined : item.reason.message))
      .filter(nonUndefined);
    const uniqErrors = [...new Set(errors)];
    uniqErrors.forEach((error) => lapi.dispatch(Notifications.error({ title: error })));
  }

  return ok;
}

async function watchContinueEvents(lapi: ListenerApiLight) {
  const { type } = await _take(
    lapi,
    creativeFolderActions.cancelButtonClicked,
    creativeFolderActions.confirmed,
  );
  if (type !== creativeFolderActions.confirmed.type) {
    throw Interrupt.UserCancelled;
  }
}

async function initModal(project_id: number): Promise<TSagaReturn<BLContext>> {
  const [
    projectsResult,
    creativeTypesResult,
    tagsResult,
    userResult,
    genreResult,
    introResult,
    conceptTypesResult,
  ] = await Promise.all([
    api.bl.loadBLProjects(),
    api.bl.loadCreativeTypes(),
    api.bl.loadTags(),
    api.oauth.user<CurrentUser>(),
    api.bl.loadBLGenres(project_id),
    api.bl.loadBLIntros(project_id),
    api.bl.loadConceptTypes(),
  ]);

  const projects = projectsResult.success ? projectsResult.data.response : undefined;
  const creativeTypes = creativeTypesResult.success ? creativeTypesResult.data.response : undefined;
  const tags = tagsResult.success ? tagsResult.data.response : undefined;
  const user = userResult.success ? userResult.data.response : undefined;
  const genres = genreResult.success ? genreResult.data.response : undefined;
  const intros = introResult.success ? introResult.data.response : undefined;
  // MYUA-16783: справочник не обязателен — без него (бэкенд без MYUA-16781, сбой запроса)
  // модалка должна открываться, поле Concept Type просто будет недоступно.
  const conceptTypes = conceptTypesResult.success ? conceptTypesResult.data.response : [];
  if (
    projects === undefined ||
    creativeTypes === undefined ||
    tags === undefined ||
    genres === undefined ||
    intros === undefined
  ) {
    return { success: false, args: {}, reason: Interrupt.API };
  }

  return {
    success: true,
    args: {},
    data: {
      projects,
      creativeTypes,
      tags,
      genres,
      intros,
      conceptTypes,
      user: {
        ...user,
        first_name: user?.first_name ?? undefined,
        last_name: user?.last_name ?? undefined,
      },
    },
  };
}

const creativeFolderModalScenario = scenarioFactoryAsync(
  async (
    lapi: ListenerApiLight,
    blOptionsPrefilled: WithMultipleValues<BLOptions>,
    plotRecognitionInfo: PlotRecognitionInfo | null,
    editableFields?: (keyof BLOptions)[],
    confirmSaga?: (data: WithMultipleValues<BLOptions>) => Promise<boolean>,
  ) => {
    lapi.dispatch(creativeFolderActions.modalOpened());
    if (editableFields) {
      lapi.dispatch(creativeFolderActions.editableFieldsUpdated({ fields: editableFields }));
    }
    lapi.dispatch(creativeFolderActions.setIsInitialized({ isInitialized: false }));
    const initResult = await initModal(blOptionsPrefilled.projectId as number);
    lapi.signal.throwIfAborted();
    if (!initResult.success) {
      lapi.dispatch(Notifications.error(Messages.initModalError()));
      throw new Error('initModalError');
    }

    const blCtx = initResult.data;
    await obtainUsers(lapi);
    lapi.signal.throwIfAborted();
    lapi.dispatch(
      creativeFolderActions.modalUpdated({
        blContext: blCtx,
        preFilled: blOptionsPrefilled,
        plotRecognitionInfo,
      }),
    );
    lapi.dispatch(creativeFolderActions.setIsInitialized({ isInitialized: true }));
    // eslint-disable-next-line no-constant-condition
    while (true) {
      await watchContinueEvents(lapi);
      lapi.signal.throwIfAborted();
      // user chose to continue
      const options = _select(lapi, getBLOptions);

      if (confirmSaga) {
        lapi.dispatch(creativeFolderActions.setConfirmInprogress({ confirmInprogress: true }));
        const validateResult: boolean = await confirmSaga(options);
        lapi.dispatch(creativeFolderActions.setConfirmInprogress({ confirmInprogress: false }));

        if (!validateResult) {
          continue;
        }
      }

      return options;
    }
  },
  null,
  (lapi) => {
    lapi.dispatch(creativeFolderActions.modalClosed());
  },
  'creativeFolderModalScenario',
);
